<template>
  <component
    :is="variantComponent"
    v-model="active"
    :tabs="tabs"
    :disable="disable"
    :tooltip="tooltip"
  >
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]>
      <slot :name="slotName" />
    </template>
  </component>
</template>

<script setup lang="ts">
import type { Component } from "vue";
import { computed } from "vue";
import PillTabs from "./PillTabs.vue";
import type { TabItem, TabVariant } from "./tabs.types";

// Tab styling variants. Each renders the same tabs/panels differently.
// (NavigationTabs uses a different, config-driven API — to be added later.)
const TAB_VARIANTS: Record<TabVariant, Component> = {
  pill: PillTabs,
};

// The active tab's name.
const active = defineModel<string | null>({ default: null });

const props = withDefaults(
  defineProps<{
    /** The tab row, in order. Each tab's panel is the slot with its name. */
    tabs: TabItem[];
    variant?: TabVariant;
    /**
     * Locks the whole switch: panels still render, but the tab cannot be
     * changed. A single entry locks itself through its own `disable`.
     */
    disable?: boolean;
    /**
     * Shown when hovering a locked tab other than the active one — e.g. to
     * explain why the switch cannot be changed.
     */
    tooltip?: string;
  }>(),
  {
    variant: "pill",
    disable: false,
    tooltip: undefined,
  },
);

const variantComponent = computed(
  () => TAB_VARIANTS[props.variant] ?? PillTabs,
);
</script>
